import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CalendarEventColorType } from '@/components/event-calendar/types';
import {
	generateUserColorClass,
	getUniqueUserDisplayNames,
	getUserDisplayName,
} from '@/utils/userColorGenerator';

/**
 * Interface for User based on the existing User type in the codebase
 */
interface User {
	_id: string;
	name?: string;
	username?: string;
	email?: string;
	active?: boolean;
}

/**
 * Interface for the Redux state (based on existing patterns in the codebase)
 */
interface RootState {
	users: User[];
}

/**
 * Hook to access users from Redux store and format them as CalendarEventColorType options
 * This allows users to be used in place of EVENT_COLOR_OPTIONS in calendar components
 *
 * @returns Array of user options formatted like EVENT_COLOR_OPTIONS with consistent colors
 */
export const useActiveUsers = (): User[] => {
	const users = useSelector((state: RootState) => state.users);

	return useMemo(
		() =>
			Array.isArray(users)
				? users.filter((user) => user && user._id && user.active !== false)
				: [],
		[users]
	);
};

/**
 * Active team members' display names keyed by user id, with emails added
 * to names that more than one person shares
 */
export const useUserDisplayNames = (): Map<string, string> => {
	const users = useActiveUsers();
	return useMemo(() => getUniqueUserDisplayNames(users), [users]);
};

export const useUserColorOptions = (): CalendarEventColorType[] => {
	const users = useActiveUsers();
	const displayNames = useUserDisplayNames();

	// Transform users into CalendarEventColorType format with memoization for performance
	const userColorOptions = useMemo(
		() =>
			users.map((user) => ({
				value: user._id, // Use user ID as the value (like color names in EVENT_COLOR_OPTIONS)
				label: displayNames.get(user._id) || getUserDisplayName(user),
				class: generateUserColorClass(user._id), // Generate consistent color class
			})),
		[users, displayNames]
	);

	return userColorOptions;
};

/**
 * Hook to get a specific user's color option by ID
 * Useful when you need to look up a single user's color information
 *
 * @param userId - The user ID to look up
 * @returns The CalendarEventColorType for the user or null if not found
 */
export const useUserColorOption = (
	userId: string
): CalendarEventColorType | null => {
	const userColorOptions = useUserColorOptions();

	return useMemo(() => {
		return (
			userColorOptions.find((option) => option.value === userId) || null
		);
	}, [userColorOptions, userId]);
};

/**
 * Hook to get user color options with an "All Users" option at the beginning
 * Useful for filters or selection dropdowns
 *
 * @param allUsersLabel - Label for the "all users" option (default: "All Users")
 * @returns Array with "All Users" option followed by individual user options
 */
export const useUserColorOptionsWithAll = (
	allUsersLabel: string = 'All Users'
): CalendarEventColorType[] => {
	const userColorOptions = useUserColorOptions();

	return useMemo(() => {
		const allOption: CalendarEventColorType = {
			value: 'all',
			label: allUsersLabel,
			class: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
		};

		return [allOption, ...userColorOptions];
	}, [userColorOptions, allUsersLabel]);
};
